<script>
import { filter, reduce, range, uniq } from 'lodash'
import chart from '@/mixins/chart'

import { faCirclePlay, faCirclePause } from '@fortawesome/free-solid-svg-icons'

export default {
  mixins: [chart],
  name: 'ChartUverExpansionMap',
  data () {
    return {
      selectedYear: null,
      playerInterval: null
    }
  },
  created () {
    this.selectedYear = this.firstYear
  },
  computed: {
    countries () {
      return filter(this.$options.tsv, { year: parseInt(this.selectedYear) })
    },
    countriesByAlpha3 () {
      return reduce(this.countries, (all, country) => {
        all[country.alpha3] = 1
        return all
      }, {})
    },
    featureColorScale () {
      return () => {
        return this.socialMode ? '#f00' : '#000'
      }
    },
    firstYear () {
      return this.yearsWithData[0]
    },
    lastYear () {
      return this.yearsWithData[this.yearsWithData.length - 1]
    },
    yearsWithData () {
      return uniq(this.$options.tsv.map(({ year }) => year)).sort()
    },
    years () {
      const years = [...this.yearsWithData]
      return range(years.shift(), years.pop() + 1)
    },
    playerIcon () {
      return this.isPlaying ? faCirclePause : faCirclePlay
    },
    isPlaying () {
      return this.playerInterval !== null
    },
    reachedLastYear () {
      return this.selectedYear === this.lastYear
    }
  },
  methods: {
    isYearDisabled (year) {
      return this.yearsWithData.indexOf(year) === -1
    },
    togglePlayer () {
      if (this.isPlaying) {
        return this.pause()
      }
      return this.play()
    },
    play () {
      if (this.reachedLastYear) {
        this.selectedYear = this.firstYear
      }
      this.pause()
      this.playerInterval = setInterval(this.nextYear, 1000)
    },
    pause () {
      clearInterval(this.playerInterval)
      this.playerInterval = null
    },
    nextYear () {
      const currentYearIndex = this.yearsWithData.indexOf(this.selectedYear)
      // Set sekected year to the next until we can't
      this.selectedYear = this.yearsWithData[currentYearIndex + 1] || this.selectedYear
      // Move to the next only if we didnt reach the end
      if (this.reachedLastYear) {
        this.pause()
      }
    },
    selectYear (year) {
      this.pause()
      this.selectedYear = year
    }
  }
}
</script>

<template>
  <div class="chart-uber-expansion p-4">
    <h4 class="text-uppercase">
      {{ chartRoute.title }}
    </h4>
    <p>Uber rapidly expanded to offer its services in more than 80 countries and territories by the height of its international operations in 2017, but has pulled out of numerous major markets, including China, Indonesia, the Philippines, Singapore, Hungary and Russia, due to commercial, legal and political challenges.</p>
    <div class="d-flex align-items-center">
      <div class="mr-2 chart-uber-expansion__player d-print-none">
        <b-button size="lg" variant="link" class="p-1 chart-uber-expansion__player__button" @click="togglePlayer">
          <fa :icon="playerIcon" fixed-width />
        </b-button>
      </div>
      <ul class="list-unstyled row no-gutters flex-nowrap flex-grow-1 mb-0 chart-uber-expansion__years">
        <li v-for="year in years"
          :key="year"
          :class="{ 'chart-uber-expansion__years__item--active': selectedYear === year }"
          class="col chart-uber-expansion__years__item">
          <b-button
            block
            class="p-0"
            variant="link"
            size="sm"
            @click="selectYear(year)"
            :disabled="isYearDisabled(year)">
            {{ year }}
          </b-button>
        </li>
      </ul>
    </div>
    <choropleth-map
      :social-mode="socialMode"
      :data="countriesByAlpha3"
      :feature-color-scale="featureColorScale"
      hide-legend
      class="chart-uber-expansion__map my-3" />
    <p class="text-muted text-right small mb-0">
      Source: An ICIJ analysis of available locations advertised on Uber’s website
    </p>
  </div>
</template>

<style lang="scss" scoped>
@import '../../variables.scss';

.chart-uber-expansion {
  overflow: hidden;
  min-height: 500px;
  height: 100%;
  width: 100%;
  position: relative;
  background: white no-repeat center center;
  display: flex;
  flex-direction: column;

  h4 {
    font-family: $jumbotron-font-family;
  }

  .chart--social-mode & {
    background-color: $dark;
  }

  .chart--active-route-view & {
    height: calc(100vh - var(--app-footer-height, 0));
  }

  &__player {
    &__button {
      line-height: 1;
      font-size: 2.5rem;
    }
  }

  &__years {
    border-radius: $border-radius;
    background: $light;

    .chart--social-mode & {
      background-color: $white;
    }

    &__item {

      .chart--social-mode & .btn {
        color: $black;

        &[disabled] {
          opacity: 0.3;
        }
      }
      &--active .btn {
        font-weight: bold;
        background: $black;
        color: $white;

        .chart--social-mode & {
          color: $white;
          background: $primary;
        }
      }

      &:first-of-type .btn {
        border-radius: $border-radius 0 0 $border-radius;
      }

      &:last-of-type .btn {
        border-radius: 0 $border-radius $border-radius 0;
      }

    }
  }

  &__map {
    flex-grow: 1;

    & :deep(.choropleth-map__main) {
      color: $light;

      .chart--social-mode & {
        color: $secondary;
      }

      .choropleth-map__main__features__item {
        opacity: 1;
        transition: 700ms;
      }
    }
  }
}
</style>

<tsv>
year	alpha3
2013	AUS
2013	CAN
2013	CHN
2013	COL
2013	FRA
2013	DEU
2013	IND
2013	ITA
2013	JPN
2013	KOR
2013	MYS
2013	MEX
2013	NLD
2013	RUS
2013	SGP
2013	ZAF
2013	SWE
2013	CHE
2013	TWN
2013	ARE
2013	GBR
2013	USA
2014	AUS
2014	AUT
2014	BHR
2014	BEL
2014	BRA
2014	BGR
2014	CAN
2014	CHL
2014	CHN
2014	COL
2014	CZE
2014	DNK
2014	EGY
2014	FIN
2014	FRA
2014	DEU
2014	GRC
2014	HKG
2014	HUN
2014	IND
2014	IDN
2014	IRL
2014	ISR
2014	ITA
2014	JPN
2014	KOR
2014	LBN
2014	MYS
2014	MEX
2014	NLD
2014	NZL
2014	NGA
2014	NOR
2014	PAN
2014	PER
2014	PHL
2014	POL
2014	PRT
2014	QAT
2014	RUS
2014	SAU
2014	SGP
2014	ZAF
2014	ESP
2014	SWE
2014	CHE
2014	TWN
2014	THA
2014	TUR
2014	ARE
2014	GBR
2014	USA
2014	VNM
2015	AUS
2015	AUT
2015	AZE
2015	BHR
2015	BLR
2015	BEL
2015	BRA
2015	BGR
2015	CAN
2015	CHL
2015	CHN
2015	COL
2015	CRI
2015	HRV
2015	CZE
2015	DNK
2015	DOM
2015	EGY
2015	EST
2015	FIN
2015	FRA
2015	DEU
2015	GRC
2015	HKG
2015	HUN
2015	IND
2015	IDN
2015	IRL
2015	ISR
2015	ITA
2015	JPN
2015	JOR
2015	KEN
2015	KOR
2015	LBN
2015	LTU
2015	MAC
2015	MYS
2015	MEX
2015	MAR
2015	NLD
2015	NZL
2015	NGA
2015	NOR
2015	PAN
2015	PER
2015	PHL
2015	POL
2015	PRT
2015	QAT
2015	ROU
2015	RUS
2015	SAU
2015	SGP
2015	SVK
2015	ZAF
2015	ESP
2015	LKA
2015	SWE
2015	CHE
2015	TWN
2015	THA
2015	TUR
2015	ARE
2015	GBR
2015	USA
2015	URY
2015	VNM
2016	ARG
2016	AUS
2016	AUT
2016	AZE
2016	BHR
2016	BGD
2016	BLR
2016	BEL
2016	BOL
2016	BRA
2016	BGR
2016	CAN
2016	CHL
2016	CHN
2016	COL
2016	CRI
2016	HRV
2016	CZE
2016	DNK
2016	DOM
2016	EGY
2016	EST
2016	FIN
2016	FRA
2016	DEU
2016	GHA
2016	GRC
2016	GTM
2016	HKG
2016	HUN
2016	IND
2016	IDN
2016	IRL
2016	ISR
2016	ITA
2016	JPN
2016	JOR
2016	KAZ
2016	KEN
2016	KOR
2016	LBN
2016	LTU
2016	MAC
2016	MYS
2016	MEX
2016	MAR
2016	NLD
2016	NZL
2016	NGA
2016	NOR
2016	PAK
2016	PAN
2016	PER
2016	PHL
2016	POL
2016	PRT
2016	QAT
2016	ROU
2016	RUS
2016	SAU
2016	SGP
2016	SVK
2016	ZAF
2016	ESP
2016	LKA
2016	SWE
2016	CHE
2016	TWN
2016	TZA
2016	THA
2016	TTO
2016	TUR
2016	UGA
2016	UKR
2016	ARE
2016	GBR
2016	USA
2016	URY
2016	VNM
2017	ARG
2017	AUS
2017	AUT
2017	AZE
2017	BHR
2017	BGD
2017	BLR
2017	BEL
2017	BOL
2017	BRA
2017	KHM
2017	CAN
2017	CHL
2017	COL
2017	CRI
2017	HRV
2017	CZE
2017	DNK
2017	DOM
2017	ECU
2017	EGY
2017	SLV
2017	EST
2017	FIN
2017	FRA
2017	DEU
2017	GHA
2017	GRC
2017	GTM
2017	HKG
2017	HUN
2017	IND
2017	IDN
2017	IRL
2017	ISR
2017	ITA
2017	JPN
2017	JOR
2017	KAZ
2017	KEN
2017	KOR
2017	LBN
2017	LTU
2017	MAC
2017	MYS
2017	MEX
2017	MAR
2017	MMR
2017	NLD
2017	NZL
2017	NGA
2017	NOR
2017	PAK
2017	PAN
2017	PER
2017	PHL
2017	POL
2017	PRT
2017	PRI
2017	QAT
2017	ROU
2017	RUS
2017	SAU
2017	SGP
2017	SVK
2017	ZAF
2017	ESP
2017	LKA
2017	SWE
2017	CHE
2017	TWN
2017	TZA
2017	THA
2017	TTO
2017	TUR
2017	UGA
2017	UKR
2017	ARE
2017	GBR
2017	USA
2017	URY
2017	VNM
2018	ARG
2018	AUS
2018	AUT
2018	AZE
2018	BHR
2018	BGD
2018	BLR
2018	BEL
2018	BOL
2018	BRA
2018	KHM
2018	CAN
2018	CHL
2018	COL
2018	CRI
2018	HRV
2018	CZE
2018	DNK
2018	DOM
2018	ECU
2018	EGY
2018	SLV
2018	EST
2018	FIN
2018	FRA
2018	DEU
2018	GHA
2018	GRC
2018	GTM
2018	HKG
2018	HUN
2018	IND
2018	IDN
2018	IRL
2018	ISR
2018	ITA
2018	JPN
2018	JOR
2018	KAZ
2018	KEN
2018	KOR
2018	LBN
2018	LTU
2018	MAC
2018	MYS
2018	MUS
2018	MEX
2018	MAR
2018	MMR
2018	NLD
2018	NZL
2018	NGA
2018	NOR
2018	PAK
2018	PAN
2018	PER
2018	PHL
2018	POL
2018	PRT
2018	PRI
2018	QAT
2018	ROU
2018	RUS
2018	SAU
2018	SGP
2018	ZAF
2018	ESP
2018	LKA
2018	SWE
2018	CHE
2018	TWN
2018	TZA
2018	THA
2018	TTO
2018	TUR
2018	UGA
2018	UKR
2018	ARE
2018	GBR
2018	USA
2018	URY
2018	VNM
2019	ARG
2019	AUS
2019	AUT
2019	BHR
2019	BGD
2019	BEL
2019	BOL
2019	BRA
2019	CAN
2019	CHL
2019	COL
2019	CRI
2019	CIV
2019	HRV
2019	CZE
2019	DNK
2019	DOM
2019	ECU
2019	EGY
2019	SLV
2019	EST
2019	FIN
2019	FRA
2019	DEU
2019	GHA
2019	GRC
2019	GTM
2019	HND
2019	HKG
2019	HUN
2019	IND
2019	IRL
2019	ISR
2019	ITA
2019	JPN
2019	JOR
2019	KEN
2019	KOR
2019	LBN
2019	LTU
2019	MAC
2019	MUS
2019	MEX
2019	MAR
2019	NLD
2019	NZL
2019	NGA
2019	NOR
2019	PAK
2019	PAN
2019	PRY
2019	PER
2019	POL
2019	PRT
2019	PRI
2019	QAT
2019	ROU
2019	SAU
2019	SVK
2019	ZAF
2019	ESP
2019	LKA
2019	SWE
2019	CHE
2019	TWN
2019	TZA
2019	TUR
2019	UGA
2019	UKR
2019	ARE
2019	GBR
2019	USA
2019	URY
2020	AND
2020	ARG
2020	AUS
2020	AUT
2020	BHR
2020	BGD
2020	BEL
2020	BOL
2020	BRA
2020	CAN
2020	CHL
2020	COL
2020	CRI
2020	CIV
2020	HRV
2020	CZE
2020	DNK
2020	DOM
2020	ECU
2020	EGY
2020	SLV
2020	EST
2020	FIN
2020	FRA
2020	DEU
2020	GHA
2020	GRC
2020	GTM
2020	HND
2020	HKG
2020	IND
2020	IRL
2020	ISR
2020	ITA
2020	JPN
2020	JOR
2020	KEN
2020	KOR
2020	LBN
2020	LTU
2020	MAC
2020	MEX
2020	MAR
2020	NLD
2020	NZL
2020	NGA
2020	NOR
2020	PAK
2020	PAN
2020	PRY
2020	PER
2020	POL
2020	PRT
2020	PRI
2020	QAT
2020	ROU
2020	SAU
2020	SVK
2020	ZAF
2020	ESP
2020	LKA
2020	SWE
2020	CHE
2020	TWN
2020	TZA
2020	TUR
2020	UGA
2020	UKR
2020	ARE
2020	GBR
2020	USA
2020	URY
2021	AND
2021	ARG
2021	AUS
2021	AUT
2021	BHR
2021	BGD
2021	BEL
2021	BOL
2021	BRA
2021	CAN
2021	CHL
2021	COL
2021	CRI
2021	CIV
2021	HRV
2021	CZE
2021	DOM
2021	ECU
2021	EGY
2021	SLV
2021	EST
2021	FIN
2021	FRA
2021	DEU
2021	GHA
2021	GRC
2021	GTM
2021	HND
2021	HKG
2021	IND
2021	IRL
2021	ISR
2021	ITA
2021	JAM
2021	JPN
2021	JOR
2021	KEN
2021	KOR
2021	LBN
2021	LTU
2021	MAC
2021	MEX
2021	MAR
2021	NLD
2021	NZL
2021	NGA
2021	NOR
2021	PAK
2021	PAN
2021	PRY
2021	PER
2021	POL
2021	PRT
2021	PRI
2021	QAT
2021	ROU
2021	SAU
2021	SVK
2021	ZAF
2021	ESP
2021	LKA
2021	SWE
2021	CHE
2021	TWN
2021	TZA
2021	TUR
2021	UGA
2021	UKR
2021	ARE
2021	GBR
2021	USA
2021	URY
2022	AND
2022	ARG
2022	AUS
2022	AUT
2022	BHR
2022	BGD
2022	BEL
2022	BOL
2022	BRA
2022	BGR
2022	CAN
2022	CHL
2022	COL
2022	CRI
2022	CIV
2022	HRV
2022	CZE
2022	DOM
2022	ECU
2022	EGY
2022	SLV
2022	EST
2022	FIN
2022	FRA
2022	DEU
2022	GHA
2022	GRC
2022	GTM
2022	HND
2022	HKG
2022	IND
2022	IRL
2022	ISR
2022	ITA
2022	JAM
2022	JPN
2022	JOR
2022	KEN
2022	KOR
2022	LBN
2022	LTU
2022	MAC
2022	MLT
2022	MEX
2022	MAR
2022	NLD
2022	NZL
2022	NGA
2022	NOR
2022	PAK
2022	PAN
2022	PRY
2022	PER
2022	POL
2022	PRT
2022	PRI
2022	QAT
2022	ROU
2022	SAU
2022	SVK
2022	ZAF
2022	ESP
2022	LKA
2022	SWE
2022	CHE
2022	TWN
2022	TZA
2022	TUR
2022	UGA
2022	UKR
2022	ARE
2022	GBR
2022	USA
2022	URY
</tsv>
